import { useId, useState, type ReactNode } from "react";
import { inputClass } from "./classes";

/** Everything a row knows, lowercased, so a filter term matches wherever it appears — displayed or not. */
function rowText(row: unknown): string {
  const parts: string[] = [];
  const walk = (v: unknown): void => {
    if (v === null || v === undefined) return;
    if (typeof v === "object") Object.values(v as Record<string, unknown>).forEach(walk);
    else parts.push(String(v));
  };
  walk(row);
  return parts.join(" ").toLowerCase();
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Right-aligned, tabular figures. */
  numeric?: boolean;
  /** Sortable by this value; the header becomes a button with aria-sort. */
  sortValue?: (row: T) => string | number;
}

type Sort = { key: string; dir: "ascending" | "descending" };

/**
 * Every list (mermera's shared-ui DataTable, with sorting made
 * keyboard-first): a real table with a caption, `scope` on headers, a
 * filter box that matches anywhere in the row, and sortable columns whose
 * header is a button that announces the order through aria-sort. The
 * caption is visible unless `captionHidden`; a screen reader always
 * gets it. On a phone the table scrolls sideways inside a labelled region
 * rather than the page.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  captionHidden = false,
  empty,
  filter = true,
  initialSort,
  rowTestId,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption: string;
  captionHidden?: boolean | undefined;
  empty: ReactNode;
  filter?: boolean | undefined;
  initialSort?: Sort | undefined;
  rowTestId?: ((row: T) => string) | undefined;
}) {
  const [term, setTerm] = useState("");
  const [sort, setSort] = useState<Sort | undefined>(initialSort);
  const inputId = useId();
  if (rows.length === 0) return <>{empty}</>;

  const needle = term.trim().toLowerCase();
  let visible = needle ? rows.filter((r) => rowText(r).includes(needle)) : rows;
  const sortCol = sort ? columns.find((c) => c.key === sort.key) : undefined;
  if (sort && sortCol?.sortValue) {
    const value = sortCol.sortValue;
    visible = [...visible].sort((a, b) => collator.compare(String(value(a)), String(value(b))) * (sort.dir === "ascending" ? 1 : -1));
  }

  const toggleSort = (key: string) =>
    setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === "ascending" ? "descending" : "ascending" } : { key, dir: "ascending" }));

  return (
    <div>
      {filter && rows.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <label htmlFor={inputId} className="sr-only">
            Filter {caption}
          </label>
          <input id={inputId} type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Filter" className={`${inputClass} w-64 max-w-full`} />
          <span className="text-sm text-muted" aria-live="polite">
            {needle ? `${visible.length} of ${rows.length} shown` : ""}
          </span>
        </div>
      )}
      {visible.length === 0 ? (
        <p className="py-6 text-sm text-muted">Nothing matches “{term.trim()}”.</p>
      ) : (
        // A scrollable region must be reachable by keyboard to scroll it sideways on a phone (WCAG 2.1.1);
        // the label tells a screen reader what it is — the one place a landmark takes a tabindex. `relative`
        // makes it the containing block for the sr-only spans inside (RelativeTime's absolute time): an
        // absolutely positioned element inside a scroll container otherwise sizes the PAGE, not the region.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div className="relative overflow-x-auto" role="region" aria-label={caption} tabIndex={0}>
          <table className="w-full text-base">
            <caption className={captionHidden ? "sr-only" : "mb-2 text-left text-sm text-muted"}>{caption}</caption>
            <thead>
              <tr className="border-b border-line text-left text-sm text-muted">
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  return (
                    <th key={c.key} scope="col" aria-sort={c.sortValue ? (active ? sort!.dir : "none") : undefined} className={`py-2 pr-4 font-medium ${c.numeric ? "text-right" : ""}`}>
                      {c.sortValue ? (
                        <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex min-h-6 items-center gap-1 font-medium text-muted underline-offset-2 hover:underline">
                          {c.header}
                          <span aria-hidden="true">{active ? (sort!.dir === "ascending" ? "▲" : "▼") : "↕"}</span>
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={rowKey(row)} data-testid={rowTestId?.(row)} className="border-b border-line last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className={`py-3 pr-4 align-middle ${c.numeric ? "text-right tabular-nums" : ""}`}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
